import React, { useEffect, useState } from "react";
import {
  Text,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from "react-native";

import global from "../../../styles/globalStyles";

import {
  InputDropdown,
  ProcedureButtonLightBlue,
  Tab,
} from "../../common/native/index";
import { TReturnGetImagesBatchBatchId } from "../../../services/api";
import { TAreaInterest, TProcedure } from "../../../utils/types";
import {
  isBodyProcedure,
  isFaceProcedure,
  isTorsoProcedure,
} from "../../../utils/functions";
import DisplayOneSquare from "./DisplayOneSquare";
import DisplayOneSquareCompare from "./DisplayOneSquareCompare";
import DisplayTwoSquares from "./DisplayTwoSquares";

type PatientDetailSimulationProps = {
  patientImageData: TReturnGetImagesBatchBatchId[];
};

const PatientDetailSimulation: React.FC<PatientDetailSimulationProps> = ({
  patientImageData,
}) => {
  const { width } = useWindowDimensions();
  const isMobile = width < 644;

  const [loading, setLoading] = useState<boolean>(true);
  const [selectedProcedure, setSelectedProcedure] = useState<TProcedure | "">(
    "",
  );
  const [allProcedures, setAllProcedures] = useState<TProcedure[]>([]);
  const [initialTab, setInitialTab] = useState<TAreaInterest | "">();
  const [selectedSimulationTab, setSelectedSimulationTab] = useState<
    "Uploaded" | "Compare" | "Side-By-Side"
  >("Uploaded");
  const [showCompareAndSideBySideTabs, setShowCompareAndSideBySideTabs] =
    useState<boolean>(true);

  const [isAllProcedureDataEmpty, setIsAllProcedureDataEmpty] =
    useState<boolean>(false);

  useEffect(() => {
    const isAllProcedureDataEmpty = patientImageData.every(
      (obj) =>
        !Array.isArray(obj.procedureType) || obj.procedureType.length === 0,
    );

    if (isAllProcedureDataEmpty) {
      setIsAllProcedureDataEmpty(true);
    } else {
      const isShowCompareAndSideBySideTabs = patientImageData.every((obj) =>
        obj.uploadType?.includes("Patient"),
      );

      setShowCompareAndSideBySideTabs(!isShowCompareAndSideBySideTabs);

      const allSimulatedProceduresDuplicate = patientImageData.flatMap(
        (item) => item.procedureType,
      );
      const allSimulatedProceduresUnique = Array.from(
        new Set(allSimulatedProceduresDuplicate),
      );
      const sortedAllSimulatedProceduresUnique =
        allSimulatedProceduresUnique.sort();
      setAllProcedures(sortedAllSimulatedProceduresUnique);

      if (allSimulatedProceduresUnique.length === 0) return;
      const firstProcedure = allSimulatedProceduresUnique[0];
      setSelectedProcedure(firstProcedure);
      if (isBodyProcedure(firstProcedure)) setInitialTab("Body");
      if (isTorsoProcedure(firstProcedure)) setInitialTab("Breast");
      if (isFaceProcedure(firstProcedure)) setInitialTab("Face");
      setLoading(false);
      setIsAllProcedureDataEmpty(false);
    }
  }, [patientImageData]);

  const renderProcedureButtons = (areaOfInterest: TAreaInterest) => {
    return allProcedures
      .filter((item) => {
        if (areaOfInterest === "Body") return isBodyProcedure(item);
        if (areaOfInterest === "Breast") return isTorsoProcedure(item);
        if (areaOfInterest === "Face") return isFaceProcedure(item);
        return false;
      })
      .map((item, index) => (
        <ProcedureButtonLightBlue
          key={index}
          procedure={item}
          selected={item === selectedProcedure}
          onPress={() => setSelectedProcedure(item)}
        />
      ));
  };

  const renderAllComparePreopAndSimulatedTabContent = () => {
    return allProcedures.map((item, index) => {
      const currentImageData = patientImageData.filter(
        (itemInner) =>
          Array.isArray(itemInner.procedureType) &&
          itemInner.procedureType.includes(item),
      );

      return (
        <View key={index}>
          <View
            style={{
              width: isMobile ? "100%" : 700,
              display:
                selectedSimulationTab === "Side-By-Side" &&
                selectedProcedure === item
                  ? "flex"
                  : "none",
            }}
          >
            <DisplayTwoSquares currentImageData={currentImageData} />
          </View>
          <View
            style={{
              width: isMobile ? "100%" : 387,
              display:
                selectedSimulationTab === "Compare" &&
                selectedProcedure === item
                  ? "flex"
                  : "none",
            }}
          >
            <DisplayOneSquareCompare
              currentImageData={currentImageData}
              selectedProcedure={item}
            />
          </View>
          <View
            style={{
              width: isMobile ? "100%" : 387,
              display:
                selectedSimulationTab === "Uploaded" &&
                selectedProcedure === item
                  ? "flex"
                  : "none",
            }}
          >
            <DisplayOneSquare
              currentImageData={currentImageData}
              isAIImages={false}
            />
          </View>
        </View>
      );
    });
  };

  const renderSimulationTab = () => {
    return (
      <View style={{ flex: 1, marginLeft: 20 }}>
        <View
          style={{
            flexDirection: "row",
            marginHorizontal: "auto",
            backgroundColor: "#F8F8F8",
            borderRadius: 10,
            marginBottom: 24,
          }}
        >
          {showCompareAndSideBySideTabs && (
            <React.Fragment>
              <Pressable
                style={[
                  styles.beforeAICompareButton,
                  selectedSimulationTab === "Side-By-Side" &&
                    styles.beforeAICompareButtonSelected,
                ]}
                onPress={() => setSelectedSimulationTab("Side-By-Side")}
              >
                <Text style={[global.text, global.fontWeight600]}>
                  Side-By-Side
                </Text>
              </Pressable>
              <Pressable
                style={[
                  styles.beforeAICompareButton,
                  selectedSimulationTab === "Compare" &&
                    styles.beforeAICompareButtonSelected,
                ]}
                onPress={() => setSelectedSimulationTab("Compare")}
              >
                <Text style={[global.text, global.fontWeight600]}>Compare</Text>
              </Pressable>
            </React.Fragment>
          )}
          <Pressable
            style={[
              styles.beforeAICompareButton,
              selectedSimulationTab === "Uploaded" &&
                styles.beforeAICompareButtonSelected,
            ]}
            onPress={() => setSelectedSimulationTab("Uploaded")}
          >
            <Text style={[global.text, global.fontWeight600]}>Uploaded</Text>
          </Pressable>
        </View>
        <View
          style={{
            marginBottom: 20,
          }}
        >
          {renderAllComparePreopAndSimulatedTabContent()}
        </View>
      </View>
    );
  };

  if (isAllProcedureDataEmpty)
    return (
      <View
        style={{
          width: isMobile ? "100%" : 387,
          marginHorizontal: "auto",
          marginTop: 20,
        }}
      >
        <DisplayOneSquare
          currentImageData={patientImageData}
          isAllProcedureDataEmpty={true}
          isAIImages={false}
        />
      </View>
    );

  if (isMobile)
    return (
      <>
        <View
          style={{
            zIndex: 1,
            marginVertical: 24,
            minWidth: isMobile ? width - 40 : "auto",
          }}
        >
          <InputDropdown
            name={""}
            selections={allProcedures}
            value={selectedProcedure}
            // @ts-ignore
            onSelect={setSelectedProcedure}
          />
        </View>
        {renderSimulationTab()}
      </>
    );

  return (
    <View style={{ flexDirection: isMobile ? "column" : "row", marginTop: 14 }}>
      {!isMobile && (
        <View
          style={{
            gap: 12,
            width: "100%",
            maxWidth: 240,
            marginTop: 32,
            marginRight: 20,
          }}
        >
          {allProcedures.map((item, index) => (
            <ProcedureButtonLightBlue
              key={index}
              procedure={item}
              selected={item === selectedProcedure}
              onPress={() => setSelectedProcedure(item)}
            />
          ))}
        </View>
      )}

      <View
        style={{
          flexGrow: 1,
          alignItems: "center",
          minWidth: isMobile ? width - 40 : undefined,
        }}
      >
        {renderSimulationTab()}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  beforeAICompareButton: {
    width: 120,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 10,
    paddingVertical: 8,
  },
  beforeAICompareButtonSelected: {
    backgroundColor: "#00B7F41A",
    borderRadius: 10,
  },
});

export default PatientDetailSimulation;
